import { useState } from 'react';
import { AlertTriangle, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { supabase } from '@/integrations/supabase/client';
import { submitForm } from '@/lib/submission';
import { toast } from 'sonner';

interface ReportErrorButtonProps {
  brandName: string;
  modelName: string;
  variantName: string;
}

export function ReportErrorButton({ brandName, modelName, variantName }: ReportErrorButtonProps) {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const carLabel = `${brandName} ${modelName} ${variantName}`;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!message.trim()) {
      toast.error('Skriv venligst hvad der er forkert');
      return;
    }

    setIsSubmitting(true);

    const fullMessage = `[Bil: ${carLabel}]\n\n${message.trim()}`;
    const payload = {
      type: 'car_error',
      message: fullMessage,
      email: email.trim() || null,
      page_url: window.location.href,
    };

    const res = await submitForm({
      formName: 'report_car_error',
      table: 'feedback',
      payload,
      email: payload.email,
      meta: { brand: brandName, model: modelName, variant: variantName },
      perform: () => supabase.from('feedback').insert(payload),
    });

    if (res.ok) {
      toast.success('Tak! Vi kigger på det hurtigst muligt.');
      setMessage('');
      setEmail('');
      setOpen(false);
    } else {
      toast.error(res.userMessage ?? 'Der opstod en fejl. Prøv venligst igen.');
    }
    setIsSubmitting(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button 
          variant="outline" 
          size="sm" 
          className="gap-2 border-orange-400 text-orange-600 hover:bg-orange-50 hover:text-orange-700 dark:border-orange-500 dark:text-orange-400 dark:hover:bg-orange-950 dark:hover:text-orange-300"
        >
          <AlertTriangle className="w-4 h-4 fill-orange-100 dark:fill-orange-900" />
          FEJL I BILDATA?
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Rapportér fejl i elbildata</DialogTitle>
          <DialogDescription>
            Har du fundet en fejl i data for {carLabel}? Fortæl os hvad der er forkert.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="error-message">Hvad er forkert?</Label>
            <Textarea
              id="error-message"
              placeholder="Fx: Prisen er forkert, rækkevidde burde være 450 km, mangler sædevarme som standard..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={4}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="error-email">Email (valgfrit)</Label>
            <Input
              id="error-email"
              type="email"
              placeholder="din@email.dk"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Hvis du vil have besked når vi har rettet fejlen
            </p>
          </div>

          <Button type="submit" className="w-full gap-2" disabled={isSubmitting}>
            <Send className="w-4 h-4" />
            {isSubmitting ? 'Sender...' : 'Send fejlrapport'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
